import type { EncounterTiming } from "@allergy123/contracts/clinical";
import { Button, Card, Status } from "@allergy123/ui";
import { useEffect, useState } from "react";
import { formatTime } from "../lib/format";

/**
 * The test timers (Q-13, C-44): the application time is persisted on the draft the moment the timer starts, and
 * the two reads are due at the panel's intervals. Notifications carry no patient information (AGENTS.md).
 */
export function TimerStep({ timing, histamineMinutes, fullMinutes, timezone, disabled, onStart }: { timing: EncounterTiming; histamineMinutes: number; fullMinutes: number; timezone: string; disabled: boolean; onStart: () => Promise<void> }) {
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const [notify, setNotify] = useState<NotificationPermission | "unsupported">(() => (typeof Notification === "undefined" ? "unsupported" : Notification.permission));
  const applied = timing.appliedAt ? new Date(timing.appliedAt).getTime() : null;

  useEffect(() => {
    if (!applied) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [applied]);

  // Local notifications while this page is open; the wording never names the patient.
  useEffect(() => {
    if (!applied || notify !== "granted") return;
    const timers = [
      [histamineMinutes, "Please read the histamine reaction."],
      [fullMinutes, "Please read all reactions."],
    ] as const;
    const handles = timers.map(([minutes, body]) => {
      const delay = applied + minutes * 60_000 - Date.now();
      return delay > 0 ? setTimeout(() => new Notification("Allergy test timer", { body, tag: `a123-timer-${minutes}` }), delay) : undefined;
    });
    return () => handles.forEach((h) => h && clearTimeout(h));
  }, [applied, notify, histamineMinutes, fullMinutes]);

  const remaining = (minutes: number) => (applied ? Math.max(0, applied + minutes * 60_000 - now) : minutes * 60_000);
  const mmss = (ms: number) => `${String(Math.floor(ms / 60_000)).padStart(2, "0")}:${String(Math.floor((ms % 60_000) / 1000)).padStart(2, "0")}`;

  async function start() {
    setBusy(true);
    try {
      await onStart();
      // Ask after the start is saved, so a pending permission prompt never delays the record.
      if (notify === "default") void Notification.requestPermission().then(setNotify);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <p className="max-w-[68ch] text-ink-secondary">Apply the tray, then start the timer. Read the histamine control at {histamineMinutes} minutes and every well at {fullMinutes} minutes. The application time is recorded with the test.</p>
      <div className="grid gap-4 sm:grid-cols-2">
        {[
          ["Histamine read", histamineMinutes],
          ["All reactions", fullMinutes],
        ].map(([label, minutes]) => {
          const ms = remaining(minutes as number);
          return (
            <Card key={label as string} dense>
              <p className="text-small text-ink-secondary">{label as string}</p>
              <p className="text-h2 font-semibold tabular-nums text-anchor" aria-live="polite">
                {applied && ms === 0 ? "Due" : mmss(ms)}
              </p>
            </Card>
          );
        })}
      </div>
      {applied ? (
        <Status kind="success" label={`Timer started at ${formatTime(timing.appliedAt!, timezone)}.`} />
      ) : (
        <div>
          <Button onClick={() => void start()} disabled={disabled || busy}>
            Start timer
          </Button>
        </div>
      )}
      {notify === "denied" && <Status kind="info" label="Browser notifications are off; keep this page visible to see the countdown." />}
      {timing.readAt && <Status kind="info" label={`Reactions read at ${formatTime(timing.readAt, timezone)}.`} />}
    </div>
  );
}
